"use client";

/**
 * Send flow for a public landing page email button: calls the guest send action, shows the result
 * toast, holds the sending and sent states for the button, and sends a signed-out viewer to sign in.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useToast } from "@/components/providers/FetchToastProvider";
import type { ToastSeverity } from "@/fetch-ui/FetchToast";
import { sendEmailButtonAction } from "../_logic/actions/email-button-send-actions";
import { EMAIL_BUTTON_SENT_STATE_MS } from "../_logic/constants";
import type { SendEmailButtonResult } from "../_logic/types/ui-types";
import { buildLandingPageAuthRedirect } from "../_logic/utils/auth-redirect";

/** The button a send is for. The server resolves the template and recipient from these ids. */
export interface EmailButtonSendTarget {
  /** Landing page the button is on */
  landingPageId: number;
  /** Event the page belongs to. Only used to build the sign-in redirect. */
  eventId: number;
  /** Id of the clicked CTA button */
  buttonId: string;
}

/** Where the button is in its send flow. */
export type EmailButtonSendStatus = "idle" | "sending" | "sent" | "redirecting";

/** Analytics outcome of one send, recorded on the landing page CTA activation event. */
export type EmailButtonSendOutcome =
  | "email_sent"
  | "email_cooldown"
  | "email_unavailable"
  | "email_error"
  | "email_signin_redirect";

/** Results that show a toast and return the button to idle. */
type IdleResultStatus = Exclude<SendEmailButtonResult["status"], "sent" | "unauthenticated">;

/** Toast copy, toast type, and analytics outcome for each result that returns the button to idle. */
const IDLE_RESULT_FEEDBACK: Record<
  IdleResultStatus,
  { message: string; type: ToastSeverity; outcome: EmailButtonSendOutcome }
> = {
  cooldown: {
    message: "Already sent a few minutes ago. Check your inbox.",
    type: "warning",
    outcome: "email_cooldown",
  },
  unavailable: { message: "This email isn't available right now.", type: "warning", outcome: "email_unavailable" },
  // Unverified and denied viewers can't receive this email. They are recorded as unavailable so
  // `email_error` stays a signal for real failures.
  unverified: {
    message: "Verify your email address to get this emailed to you.",
    type: "error",
    outcome: "email_unavailable",
  },
  denied: { message: "You don't have access to this email.", type: "error", outcome: "email_unavailable" },
  error: { message: "Couldn't send the email. Try again.", type: "error", outcome: "email_error" },
};

/** What {@link useEmailButtonSend} returns. */
interface UseEmailButtonSendResult {
  /** Current step of the send flow */
  status: EmailButtonSendStatus;
  /** Starts a send. Does nothing unless the status is idle. */
  send: () => Promise<void>;
}

/**
 * Runs the email button send flow: idle, sending, then sent (for {@link EMAIL_BUTTON_SENT_STATE_MS})
 * or back to idle with a toast. A signed-out viewer is sent to sign in and returned to this page.
 * Clicks while not idle are ignored, so a double click sends once.
 *
 * @param target - The landing page, event, and button the send is for
 * @param onOutcome - Optional analytics callback, called once per completed send with its outcome
 * @returns The current status and the `send` function
 * @throws If called outside a FetchToastProvider
 */
export function useEmailButtonSend(
  target: EmailButtonSendTarget,
  onOutcome?: (outcome: EmailButtonSendOutcome) => void
): UseEmailButtonSendResult {
  const { landingPageId, eventId, buttonId } = target;

  // Hooks
  const { showToast } = useToast();

  // States
  const [status, setStatus] = useState<EmailButtonSendStatus>("idle");
  // Mirrors `status` synchronously, so two clicks in the same tick can't both start a send.
  const statusRef = useRef<EmailButtonSendStatus>("idle");
  const sentTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(false);

  // Handlers
  const updateStatus = useCallback((next: EmailButtonSendStatus) => {
    statusRef.current = next;
    if (isMountedRef.current) setStatus(next);
  }, []);

  const send = useCallback(async (): Promise<void> => {
    if (statusRef.current !== "idle") return;
    updateStatus("sending");

    let result: SendEmailButtonResult;
    try {
      result = await sendEmailButtonAction({ landingPageId, buttonId });
    } catch (error) {
      console.error("useEmailButtonSend: send action failed", { landingPageId, buttonId }, error);
      result = { status: "error" };
    }

    if (result.status === "unauthenticated") {
      onOutcome?.("email_signin_redirect");
      updateStatus("redirecting");
      const nextPath = `${window.location.pathname}${window.location.search}`;
      window.location.assign(buildLandingPageAuthRedirect(null, nextPath, eventId));
      return;
    }

    // The toast and analytics still fire if the button unmounted mid-send (the provider outlives it);
    // only the button's own state and timer are skipped.
    if (result.status === "sent") {
      showToast(`Sent to ${result.email}. Check spam if you don't see it.`, "success");
      onOutcome?.("email_sent");
      updateStatus("sent");
      if (!isMountedRef.current) return;

      sentTimerRef.current = setTimeout(() => {
        sentTimerRef.current = null;
        updateStatus("idle");
      }, EMAIL_BUTTON_SENT_STATE_MS);
      return;
    }

    const feedback = IDLE_RESULT_FEEDBACK[result.status];
    showToast(feedback.message, feedback.type);
    onOutcome?.(feedback.outcome);
    updateStatus("idle");
  }, [buttonId, eventId, landingPageId, onOutcome, showToast, updateStatus]);

  // Effects
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (sentTimerRef.current) {
        clearTimeout(sentTimerRef.current);
        sentTimerRef.current = null;
      }
    };
  }, []);

  return { status, send };
}
